/**
 * Path-graph helpers shared by the ped AI and the traffic AI (allocation-free).
 * Lane ids are resolved through a per-graph cache so `PathLane.id` never has to
 * equal the array index.
 */
import type { PathGraph, PathLane } from './types';
import { angleDiff, clamp } from './util';

const indexCache = new WeakMap<PathGraph, Map<number, number> | null>();
const reverseCache = new WeakMap<PathGraph, Int32Array>();

/** array index of a lane id (-1 when unknown) */
export function laneIndex(g: PathGraph, id: number): number {
  const lanes = g.lanes;
  if (id >= 0 && id < lanes.length && lanes[id].id === id) return id;
  let m = indexCache.get(g);
  if (m === undefined) {
    m = new Map();
    for (let i = 0; i < lanes.length; i++) m.set(lanes[i].id, i);
    indexCache.set(g, m);
  }
  const i = m ? m.get(id) : undefined;
  return i === undefined ? -1 : i;
}

/** heading (yaw) of the lane direction a -> b */
export function laneHeading(l: PathLane): number {
  return Math.atan2(l.b.x - l.a.x, l.b.z - l.a.z);
}

/** squared distance from the last projectT() query point to the lane */
export let projDist2 = 0;

/** parameter t in 0..1 of the closest point on the lane; also sets `projDist2` */
export function projectT(l: PathLane, x: number, z: number): number {
  const ax = l.a.x, az = l.a.z;
  const dx = l.b.x - ax, dz = l.b.z - az;
  const l2 = dx * dx + dz * dz;
  if (l2 < 1e-6) {
    const ex = x - ax, ez = z - az;
    projDist2 = ex * ex + ez * ez;
    return 1;
  }
  const t = clamp(((x - ax) * dx + (z - az) * dz) / l2, 0, 1);
  const px = ax + dx * t - x, pz = az + dz * t - z;
  projDist2 = px * px + pz * pz;
  return t;
}

/**
 * Pick the lane (array index) to follow after `laneIdx` ends.
 * With probability `straightP` a lane continuing roughly straight is chosen when one exists;
 * U-turns are avoided unless they are the only option. Returns -1 for a dead end.
 */
export function chooseNext(g: PathGraph, laneIdx: number, rng: () => number, straightP: number): number {
  const lanes = g.lanes;
  const lane = lanes[laneIdx];
  if (!lane) return -1;
  const n = lane.next;
  if (!n || n.length === 0) return -1;
  const h = laneHeading(lane);
  let straight = 0, valid = 0, nonU = 0;
  for (let i = 0; i < n.length; i++) {
    const idx = laneIndex(g, n[i]);
    if (idx < 0) continue;
    valid++;
    const d = Math.abs(angleDiff(h, laneHeading(lanes[idx])));
    if (d < 0.4) straight++;
    if (d < 2.6) nonU++;
  }
  if (valid === 0) return -1;
  if (straight > 0 && rng() < straightP) {
    let k = Math.floor(rng() * straight);
    for (let i = 0; i < n.length; i++) {
      const idx = laneIndex(g, n[i]);
      if (idx < 0) continue;
      if (Math.abs(angleDiff(h, laneHeading(lanes[idx]))) < 0.4 && k-- === 0) return idx;
    }
  }
  const pool = nonU > 0 ? nonU : valid;
  let k = Math.floor(rng() * pool);
  for (let i = 0; i < n.length; i++) {
    const idx = laneIndex(g, n[i]);
    if (idx < 0) continue;
    if (nonU > 0 && Math.abs(angleDiff(h, laneHeading(lanes[idx]))) >= 2.6) continue;
    if (k-- === 0) return idx;
  }
  return -1;
}

/** index of the lane running b -> a of `laneIdx` (bidirectional graphs), -1 if none. Cached per graph. */
export function reverseLane(g: PathGraph, laneIdx: number): number {
  const lanes = g.lanes;
  let cache = reverseCache.get(g);
  if (!cache || cache.length !== lanes.length) {
    cache = new Int32Array(lanes.length).fill(-2);
    reverseCache.set(g, cache);
  }
  if (cache[laneIdx] !== -2) return cache[laneIdx];
  const l = lanes[laneIdx];
  let best = -1, bd = 0.6 * 0.6;
  for (let i = 0; i < lanes.length; i++) {
    if (i === laneIdx) continue;
    const m = lanes[i];
    const d1 = (m.a.x - l.b.x) ** 2 + (m.a.z - l.b.z) ** 2;
    if (d1 > bd) continue;
    const d2 = (m.b.x - l.a.x) ** 2 + (m.b.z - l.a.z) ** 2;
    if (d2 > bd) continue;
    if (d1 + d2 < bd * 2) {
      best = i;
      bd = (d1 + d2) / 2;
    }
  }
  cache[laneIdx] = best;
  return best;
}

/** random lane index (-1 when the graph is empty) */
export function randomLane(g: PathGraph, rng: () => number): number {
  const n = g.lanes.length;
  return n === 0 ? -1 : Math.min(n - 1, Math.floor(rng() * n));
}
